import {
    HttpInterceptorFn,
    HttpRequest,
    HttpHandlerFn,
    HttpErrorResponse,
    HttpEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
    BehaviorSubject,
    catchError,
    filter,
    switchMap,
    take,
    throwError,
} from 'rxjs';
import { Observable } from 'rxjs';
import { LoginService } from './login.service';

let isRefreshing = false;
const refreshToken$ = new BehaviorSubject<string | null>(null);

export const AuthInterceptor: HttpInterceptorFn = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
    const router = inject(Router);
    const toastr = inject(ToastrService);
    const loginService = inject(LoginService);

    const addToken = (r: HttpRequest<unknown>, token: string) =>
        r.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

    const token = sessionStorage.getItem('token');

    const authReq =
        token && req.url.startsWith('http') && !req.headers.has('Authorization')
            ? addToken(req, token)
            : req;

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            const isRefreshCall = req.url.includes('/refresh');
            const isLoginCall = req.url.includes('/login');

            if (error.status !== 401 || isRefreshCall || isLoginCall) {
                return throwError(() => error);
            }

            if (isRefreshing) {
                // Queue: wait for the refresh to complete, then retry with the new token
                return refreshToken$.pipe(
                    filter((t): t is string => t !== null),
                    take(1),
                    switchMap((newToken) => next(addToken(req, newToken))),
                );
            }

            isRefreshing = true;
            refreshToken$.next(null); // block the queue

            return loginService.refreshToken().pipe(
                switchMap((response) => {
                    isRefreshing = false;
                    refreshToken$.next(response.token); // unblock all queued requests
                    return next(addToken(req, response.token));
                }),
                catchError((refreshError) => {
                    isRefreshing = false;
                    refreshToken$.next(null);
                    sessionStorage.removeItem('token');
                    sessionStorage.removeItem('user');
                    toastr.info(
                        'Your session has expired. Please log in again.',
                        'Session Expired',
                    );
                    router.navigate(['/login']);
                    return throwError(() => refreshError);
                }),
            );
        }),
    );
};
